/**
 * ICS 214 (Unit Activity Log) API Service
 *
 * Provides typed API methods for interacting with the ICS 214 backend endpoints.
 */

import { api } from './api';
import {
  ICS214UnitLog,
  ActivityEntry,
  RosterEntry,
  NextAction,
  PaginatedUnitLogs,
  CreateUnitLogData,
  UpdateUnitLogData,
  CreateActivityEntryData,
  UpdateActivityEntryData,
  CreateRosterEntryData,
  UpdateRosterEntryData,
  CreateNextActionData,
  UpdateNextActionData,
  UnitLogFilters,
} from '@/types/ics214';

// Backend wraps responses in { data: ... }
type ApiResponse<T> = { data: T };

export const ics214Service = {
  // ===========================================================================
  // Unit Logs
  // ===========================================================================

  list: (filters?: UnitLogFilters & { page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.incident_id) params.append('incident_id', filters.incident_id);
      if (filters.operational_period_id) params.append('operational_period_id', filters.operational_period_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.is_non_incident !== undefined) params.append('is_non_incident', String(filters.is_non_incident));
      if (filters.search) params.append('search', filters.search);
      if (filters.page) params.append('page', String(filters.page));
      if (filters.limit) params.append('limit', String(filters.limit));
    }
    const query = params.toString();
    return api.get<PaginatedUnitLogs>(`/ics214${query ? `?${query}` : ''}`);
  },

  getById: (id: string) =>
    api.get<ICS214UnitLog>(`/ics214/${id}`),

  create: (data: CreateUnitLogData) =>
    api.post<ICS214UnitLog>('/ics214', data),

  update: (id: string, data: UpdateUnitLogData) =>
    api.patch<ICS214UnitLog>(`/ics214/${id}`, data),

  delete: (id: string) =>
    api.delete(`/ics214/${id}`),

  finalize: (id: string, handoffSummary?: string) =>
    api.post<ICS214UnitLog>(`/ics214/${id}/finalize`, {
      handoff_summary: handoffSummary,
    }),

  // ===========================================================================
  // Activity Entries
  // ===========================================================================

  listEntries: (unitLogId: string) =>
    api.get<ActivityEntry[]>(`/ics214/${unitLogId}/entries`),

  createEntry: (unitLogId: string, data: CreateActivityEntryData) =>
    api.post<ActivityEntry>(`/ics214/${unitLogId}/entries`, data),

  updateEntry: (unitLogId: string, entryId: string, data: UpdateActivityEntryData) =>
    api.patch<ActivityEntry>(`/ics214/${unitLogId}/entries/${entryId}`, data),

  // The route mandates a non-empty `reason` body (deleteEntrySchema); it is
  // persisted to ics214_audit_exceptions. A bodyless DELETE here always 400s.
  deleteEntry: (unitLogId: string, entryId: string, reason: string) =>
    api.delete(`/ics214/${unitLogId}/entries/${entryId}`, { reason }),

  // ===========================================================================
  // Roster
  // ===========================================================================

  listRoster: (unitLogId: string) =>
    api.get<RosterEntry[]>(`/ics214/${unitLogId}/roster`),

  addRosterEntry: (unitLogId: string, data: CreateRosterEntryData) =>
    api.post<RosterEntry>(`/ics214/${unitLogId}/roster`, data),

  updateRosterEntry: (unitLogId: string, rosterId: string, data: UpdateRosterEntryData) =>
    api.patch<RosterEntry>(`/ics214/${unitLogId}/roster/${rosterId}`, data),

  removeRosterEntry: (unitLogId: string, rosterId: string) =>
    api.delete(`/ics214/${unitLogId}/roster/${rosterId}`),

  checkIn: (unitLogId: string, rosterId: string) =>
    api.post<RosterEntry>(`/ics214/${unitLogId}/roster/${rosterId}/check-in`),

  checkOut: (unitLogId: string, rosterId: string) =>
    api.post<RosterEntry>(`/ics214/${unitLogId}/roster/${rosterId}/check-out`),

  // ===========================================================================
  // Next Actions
  // ===========================================================================

  listNextActions: (unitLogId: string) =>
    api.get<NextAction[]>(`/ics214/${unitLogId}/next-actions`),

  createNextAction: (unitLogId: string, data: CreateNextActionData) =>
    api.post<NextAction>(`/ics214/${unitLogId}/next-actions`, data),

  updateNextAction: (unitLogId: string, actionId: string, data: UpdateNextActionData) =>
    api.patch<NextAction>(`/ics214/${unitLogId}/next-actions/${actionId}`, data),

  deleteNextAction: (unitLogId: string, actionId: string) =>
    api.delete(`/ics214/${unitLogId}/next-actions/${actionId}`),

  // ===========================================================================
  // Export
  // ===========================================================================

  exportPdf: (unitLogId: string) =>
    api.getBlob(`/ics214/${unitLogId}/export/pdf`),
};
